import { HttpErrorResponse } from '@angular/common/http';
import swal, { SweetAlertOptions } from 'sweetalert2';

import { ensureEndsWith } from '../string-helpers/ensure-ends-with';

/**
 * Interface for ErrorMessage class (which are in the ErrorCollection)
 */
interface ErrorMessage {
	payLoad?: any;
	severity: number;
	message: string;
}

/**
 * Interface for the ErrorCollection class which is returned for failed ActionResults
 */
interface ErrorCollection {
	errors: ErrorMessage[];
}

/**
 * Interface for the DotNet core error object
 */
interface DotNetCoreError {
	errors: { [key: string]: string[] };
	status: number;
	title: string;
	traceId: string;
}

type errorType = string | ErrorCollection | DotNetCoreError | Response | HttpErrorResponse | Error;

/**
 * Checks whether the given object is a DotNet core error object
 * @param errorObject
 */
function isDotNetCoreError(errorObject: any): errorObject is DotNetCoreError {
	return errorObject.errors && errorObject.traceId;
}

/**
 * Checks whether the given object is a ActionResult error object
 * @param errorObject
 */
function isActionResultError(errorObject: any): errorObject is ErrorCollection {
	return Array.isArray(errorObject.errors) && errorObject.errors[0] && !isNaN(errorObject.errors[0].severity);
}

/**
 * Checks whether the given object is a ActionResult error object
 * @param errorObject
 */
function isHtmlErrorPage(errorObject: any): errorObject is ErrorCollection {
	return errorObject.type === 'text/html';
}

/**
 * Get sweet alert options for .net core error messages
 */
function getDotNetCoreErrorMessage(errorObject: DotNetCoreError): SweetAlertOptions {
	const errorMessage = Object.values(errorObject.errors)
		.reduce((pv, cv) => pv.concat(cv), [])
		.join('; ');

	return {
		title: errorObject.title,
		text: replaceHtmlWithTitle(errorMessage),
	};
}

/**
 * The Asamco ActionResult class returns the ErrorCollection class which contains the error messages
 */
function getActionResultErrorMessage(errorObject: ErrorCollection): SweetAlertOptions {
	const errorMessage = errorObject.errors.map(er => ensureEndsWith(er.message, '.')).join('; ');
	return {
		text: replaceHtmlWithTitle(errorMessage),
	};
}

// extract title if a whole HTML page is dumped into the swal
function replaceHtmlWithTitle(input: string) {
	return input.replace(/<!DOCTYPE html>((.|\n)*)<html((.|\n)*)<title((\w|\s)*)>((\w|\s)*)<\/title>((.|\n)*)<\/html>/gi, '$7');
}

export const errorSwalContext = {
	assistanceModal: null
};

export const errorSwalGlobalSweetAlertOptions: SweetAlertOptions = {};

/**
 * Helper function to show an error swal for error responses.
 * @param error
 */
export function showErrorSwal(error: errorType, overrideOptions?: SweetAlertOptions): string {
	// The real error message
	const sweetAlertOptions: SweetAlertOptions = getErrorSwal(error, overrideOptions);

	if (!sweetAlertOptions.width && sweetAlertOptions.text && (<any>sweetAlertOptions.text).length > 200) {
		//sweetAlertOptions.width = '60vw';
	}

	if(errorSwalContext.assistanceModal) {
		errorSwalContext.assistanceModal.show(sweetAlertOptions.title, sweetAlertOptions.text);
	} else {
		swal.fire(sweetAlertOptions);
	}

	return sweetAlertOptions.text.toString();
}

/**
 * Helper function to show an error swal for error responses.
 * @param error
 */
export function showErrorSwalHtml(error: errorType, overrideOptions?: SweetAlertOptions): string {
    // The real error message
    const sweetAlertOptions: SweetAlertOptions = getErrorSwal(error, overrideOptions);

    // Convert newlines to <br> for HTML
    if (sweetAlertOptions.text) {
        const textWithNewlines = sweetAlertOptions.text.toString().replace(/\n/g, '<br>');
        sweetAlertOptions.html = textWithNewlines;
        delete sweetAlertOptions.text; // Remove the text property to avoid conflicts
    }

    if (!sweetAlertOptions.width && sweetAlertOptions.html && (<any>sweetAlertOptions.html).length > 200) {
        sweetAlertOptions.width = '60vw';
    }

    // Show error object
    swal.fire(sweetAlertOptions);

    return sweetAlertOptions.html.toString();
}

/**
 * Helper function to return an error swal options for error responses.
 * @param error
 */
export function getErrorSwal(error: errorType, overrideOptions?: SweetAlertOptions): SweetAlertOptions {
	// Temp object for parsing the error argument
	let errorObject: errorType = error;

	if (error instanceof Response) {
		errorObject = error;
	} else if (error instanceof HttpErrorResponse) {
		errorObject = error.error;
	} else if (error instanceof Error) {
		errorObject = ensureEndsWith(error.message, '.');
	} else {
		errorObject = error;
	}

	// The real error message
	let sweetAlertOptions: SweetAlertOptions = { text: '', title: '', icon: 'error' };

	// Unable to handle a NULL error object, atleast let the user know that something went wrong
	if (errorObject == null || isHtmlErrorPage(errorObject)) {
		const text = 'Error occured, please try again later or contact an administrator if the problem persists.';
		Object.assign(sweetAlertOptions, {
			text: text,
			icon: 'error',
		});
		return sweetAlertOptions;
	}

	// Handle the different error objects
	if (typeof errorObject === 'string') {
		sweetAlertOptions = { text: replaceHtmlWithTitle(errorObject) };
	} else if (isDotNetCoreError(errorObject)) {
		sweetAlertOptions = getDotNetCoreErrorMessage(errorObject);
	} else if (isActionResultError(errorObject)) {
		sweetAlertOptions = getActionResultErrorMessage(errorObject);
	}

	sweetAlertOptions = <any>{
		...sweetAlertOptions,
		icon: 'error',
		...errorSwalGlobalSweetAlertOptions,
	};
	if (overrideOptions) {
		sweetAlertOptions = <any>{
			...sweetAlertOptions,
			...overrideOptions,
		};
	}
	return sweetAlertOptions;
}
